import { type ApiErrorCode, isApiErrorBody } from '@esportsbudget/shared';

/**
 * HTTP-клиент.
 *
 * Access-токен живёт 15 минут в памяти вкладки (не в localStorage — так его
 * не достанет чужой скрипт). Refresh-токен лежит в httpOnly-cookie, поэтому
 * при 401 клиент сам молча обновляет доступ и повторяет запрос.
 */

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  readonly fields?: { path: string; message: string }[];

  constructor(
    code: ApiErrorCode,
    status: number,
    message: string,
    details?: Record<string, unknown>,
    fields?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.fields = fields;
  }
}

let accessToken: string | null = null;
let activeWorkspaceId: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setActiveWorkspaceId(id: string | null): void {
  activeWorkspaceId = id;
  try {
    if (id) localStorage.setItem('eb-workspace', id);
    else localStorage.removeItem('eb-workspace');
  } catch {
    /* приватный режим — работаем без запоминания */
  }
}

export function getActiveWorkspaceId(): string | null {
  if (activeWorkspaceId) return activeWorkspaceId;
  try {
    activeWorkspaceId = localStorage.getItem('eb-workspace');
  } catch {
    activeWorkspaceId = null;
  }
  return activeWorkspaceId;
}

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** FormData отправляем как есть, без Content-Type — его выставит браузер. */
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Не пытаться обновить токен при 401 (используется самим refresh). */
  skipRefresh?: boolean;
  /** Не подставлять заголовок воркспейса. */
  skipWorkspace?: boolean;
}

/**
 * Префикс приложения. В обычной сборке это '/', при сборке под подпуть Vite
 * подставляет сюда этот подпуть. Приводим к виду без завершающего слэша.
 */
const BASE = (import.meta.env.BASE_URL || '/').replace(/[/]+$/, '');

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const withApi = path.startsWith('/api') ? path : `/api${path}`;
  const url = `${BASE}${withApi}`;
  if (!query) return url;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseError(response: Response): Promise<ApiError> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    /* тело не JSON — ниже отдадим общий текст */
  }

  if (isApiErrorBody(payload)) {
    return new ApiError(
      payload.error.code,
      response.status,
      payload.error.message,
      payload.error.details,
      payload.error.fields,
    );
  }

  return new ApiError('INTERNAL_ERROR', response.status, `HTTP ${response.status}`);
}

/** Обновление access-токена. Параллельные 401 ждут один общий запрос. */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { accessToken?: string };
      if (!data.accessToken) return false;
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Сбрасываем через микротаск, чтобы ожидающие успели прочитать результат.
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();

  return refreshPromise;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, query, signal, skipRefresh, skipWorkspace } = options;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    if (!skipWorkspace) {
      const workspaceId = getActiveWorkspaceId();
      if (workspaceId) headers['X-Workspace-Id'] = workspaceId;
    }
    if (!formData && body !== undefined) headers['Content-Type'] = 'application/json';

    return fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: 'include',
      signal,
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
    });
  };

  let response = await send();

  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send();
    } else {
      accessToken = null;
      onUnauthorized();
    }
  }

  if (!response.ok) throw await parseError(response);

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await response.text()) as T;

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'GET', query }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: options?.method ?? 'POST', formData }),
};

/**
 * Скачивание защищённого файла: обычная ссылка не подойдёт, потому что
 * нужен заголовок с токеном.
 */
export async function downloadFile(path: string, fileName: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const workspaceId = getActiveWorkspaceId();
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;

  let response = await fetch(buildUrl(path), { headers, credentials: 'include' });
  if (response.status === 401 && (await refreshAccessToken())) {
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    response = await fetch(buildUrl(path), { headers, credentials: 'include' });
  }
  if (!response.ok) throw await parseError(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Ссылка на защищённый файл для показа в <img>/<iframe>: возвращает blob-URL. */
export async function fetchBlobUrl(path: string): Promise<string> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const workspaceId = getActiveWorkspaceId();
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;

  let response = await fetch(buildUrl(path), { headers, credentials: 'include' });
  if (response.status === 401 && (await refreshAccessToken())) {
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    response = await fetch(buildUrl(path), { headers, credentials: 'include' });
  }
  if (!response.ok) throw await parseError(response);

  return URL.createObjectURL(await response.blob());
}
